/**
 * The three things a room needs once it exists.
 *
 * Where the game actually is (the lobby card), who is waiting for a seat, and
 * a way to say you have gone to make tea rather than gone home. All three
 * were being done in chat, where they scroll away and get asked again for
 * whoever arrives next.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import type { Party } from '@/lib/types';
import { cn } from '@/lib/cn';
import { copyText } from '@/lib/clipboard';
import { ago } from '@/lib/notifications';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { OpenGame } from './OpenGame';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { Button, Field, Modal, useToast } from './ui';

/** Puts a changed party straight into the cache, so the room updates at once. */
function useParty(partyId: string) {
  const queryClient = useQueryClient();
  return (party: Party) => {
    queryClient.setQueryData(['party', partyId], (old: { party: Party } | undefined) =>
      old ? { ...old, party } : old);
    void queryClient.invalidateQueries({ queryKey: ['party', partyId] });
  };
}

/* ------------------------------------------------------------ lobby card */

export function LobbyCard({ party, canManage }: { party: Party; canManage: boolean }) {
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const lobby = party.lobby;

  if (!party.isMember) return null;
  if (!lobby && !canManage) return null;

  const copy = (what: string, label: string) => {
    void copyText(what).then((ok) => toast(ok ? label + ' copied' : 'Could not copy', ok ? 'good' : 'bad'));
  };

  return (
    <section
      aria-labelledby="party-lobby-title"
      className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[inset_0_0_0_1px_var(--line-soft)]"
    >
      <div className="flex items-center gap-2">
        <Icon name="key" size={15} className="flex-none text-accent" />
        <h3 id="party-lobby-title" className="m-0 flex-1 text-[13.5px] font-bold text-ink">Where the game is</h3>
        {canManage ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[12px] font-semibold text-mute outline-none transition-colors hover:text-ink focus-visible:text-accent"
          >
            {lobby ? 'Change' : 'Add'}
          </button>
        ) : null}
      </div>

      {lobby ? (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {lobby.server ? (
            <Row label="Server" value={lobby.server} onCopy={() => copy(lobby.server, 'Server')} />
          ) : null}
          {lobby.code ? (
            <Row label="Code" value={lobby.code} mono onCopy={() => copy(lobby.code, 'Code')} />
          ) : null}
          {lobby.notes ? <p className="m-0 text-[12.5px] leading-relaxed text-dim">{lobby.notes}</p> : null}
          {/* The code is only half of it: everybody still has to find the
              game. One press does that, through whichever launcher they
              own it on. */}
          <OpenGame gameId={party.gameId} className="mt-0.5" />
          <p className="m-0 text-[11px] text-ghost">Updated {ago(lobby.updatedAt)}</p>
        </div>
      ) : (
        <>
          <p className="m-0 mt-2 text-[12.5px] text-mute">
            Put the server and the lobby code here and everybody who joins can see them, instead of asking in chat.
          </p>
          <OpenGame gameId={party.gameId} className="mt-2.5" />
        </>
      )}

      {editing ? <EditLobby party={party} onClose={() => setEditing(false)} /> : null}
    </section>
  );
}

function Row({ label, value, mono, onCopy }: { label: string; value: string; mono?: boolean; onCopy: () => void }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      className="group flex items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-1.5 text-left outline-none transition-colors hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
    >
      <span className="w-[54px] flex-none text-[11.5px] font-semibold text-mute uppercase">{label}</span>
      <span className={cn('min-w-0 flex-1 truncate text-[13px] font-bold text-ink', mono && 'font-mono tracking-wide')}>{value}</span>
      <Icon name="copy" size={13} className="flex-none text-ghost transition-colors group-hover:text-accent" />
    </button>
  );
}

function EditLobby({ party, onClose }: { party: Party; onClose: () => void }) {
  const [server, setServer] = useState(party.lobby?.server ?? '');
  const [code, setCode] = useState(party.lobby?.code ?? '');
  const [notes, setNotes] = useState(party.lobby?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const put = useParty(party.id);
  const toast = useToast();

  async function save() {
    setBusy(true);
    try {
      const res = await api.patch<{ party: Party }>('/api/parties/' + party.id + '/lobby', { server, code, notes });
      put(res.party);
      toast('Everybody in the party can see it', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const field = 'h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] text-ink outline-none transition-colors placeholder:text-ghost focus:border-accent';

  return (
    <Modal
      title="Where the game is"
      subtitle="Only people in this party can see it."
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Server or region" hint="“EU West 3”, “my Minecraft server”, an address.">
          <input autoFocus value={server} onChange={(e) => setServer(e.target.value.slice(0, 60))} placeholder="EU West" className={field} />
        </Field>
        <Field label="Lobby code" hint="Whatever people type to get in.">
          <input value={code} onChange={(e) => setCode(e.target.value.slice(0, 40))} placeholder="4K7-P2Q" className={cn(field, 'font-mono tracking-wide')} />
        </Field>
        <Field label="How to join" hint={notes.length + '/200'}>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 200))}
            placeholder="Custom game, private lobby, password in the code above."
            className="w-full resize-none rounded-[var(--radius-s)] border border-line bg-surface-1 p-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------ waiting */

/** Who is queueing, for the people already in the room. */
export function WaitingList({ party }: { party: Party }) {
  if (!party.isMember || !(party.waitlist ?? []).length) return null;
  return (
    <section
      aria-labelledby="party-waiting-title"
      className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[inset_0_0_0_1px_var(--line-soft)]"
    >
      <div className="flex items-center gap-2">
        <Icon name="hourglass" size={15} className="flex-none text-accent" />
        <h3 id="party-waiting-title" className="m-0 flex-1 text-[13.5px] font-bold text-ink">Waiting for a seat</h3>
        <span className="text-[12px] font-semibold text-mute tabular-nums">{party.waitlist!.length}</span>
      </div>
      <ul className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
        {party.waitlist!.slice(0, 6).map((row, i) => (
          <li key={row.user.id} className="flex items-center gap-2">
            <span className="w-4 flex-none text-[11.5px] font-bold text-ghost tabular-nums">{i + 1}</span>
            <Avatar user={row.user} size={22} />
            <UserName user={row.user} className="min-w-0 flex-1 truncate text-[12.5px] font-semibold" />
            <span className="flex-none text-[11px] text-ghost">{ago(row.at)}</span>
          </li>
        ))}
      </ul>
      <p className="m-0 mt-2 text-[11.5px] text-ghost">
        The first in line is told the moment a seat frees. Nothing is held for them.
      </p>
    </section>
  );
}

/** The button for everybody else: join the queue for a full party. */
export function WaitButton({ party, className }: { party: Party; className?: string }) {
  const [waiting, setWaiting] = useState(!!party.imWaiting);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const me = useSession((s) => s.me);

  useEffect(() => setWaiting(!!party.imWaiting), [party.imWaiting]);

  if (!me || party.isMember || party.closed) return null;
  if (party.memberCount < party.size) return null;

  async function toggle() {
    setBusy(true);
    try {
      const res = await api.post<{ waiting: boolean; place: number }>('/api/parties/' + party.id + '/waitlist', { waiting: !waiting });
      setWaiting(res.waiting);
      toast(res.waiting ? 'You are number ' + res.place + ' in the queue' : 'You left the queue', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      kind={waiting ? 'outline' : 'primary'}
      size="sm"
      disabled={busy}
      className={className}
      icon={<Icon name="hourglass" size={15} />}
      onClick={() => void toggle()}
    >
      {waiting ? 'Waiting for a seat' : 'Tell me when a seat frees'}
    </Button>
  );
}

/* ------------------------------------------------------------ back soon */

const SPANS = [2, 5, 10, 20];

/** "Back in five minutes", which is not the same as having left. */
export function BackSoon({ party }: { party: Party }) {
  const me = useSession((s) => s.me);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const put = useParty(party.id);
  const toast = useToast();
  const [, tick] = useState(0);

  const mine = party.members.find((m) => m.userId === me?.id);
  const away = mine?.back && mine.back.until > Date.now() ? mine.back : null;

  /* Counts down, so the label is never a minute out of date. */
  useEffect(() => {
    if (!away) return;
    const id = window.setInterval(() => tick((n) => n + 1), 20_000);
    return () => window.clearInterval(id);
  }, [away]);

  if (!party.isMember || !me) return null;

  async function set(minutes: number) {
    setBusy(true);
    try {
      const res = await api.post<{ party: Party }>('/api/parties/' + party.id + '/brb', { minutes });
      put(res.party);
      toast(minutes ? 'The party knows you are back in ' + minutes + ' minutes' : 'Welcome back', 'good');
      setOpen(false);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (away) {
    const left = Math.max(1, Math.round((away.until - Date.now()) / 60000));
    return (
      <Button kind="outline" size="sm" disabled={busy} icon={<Icon name="clock" size={15} />} onClick={() => void set(0)}>
        Back in {left}m — I am here
      </Button>
    );
  }

  return (
    <div className="relative">
      <Button kind="outline" size="sm" disabled={busy} icon={<Icon name="clock" size={15} />} onClick={() => setOpen((v) => !v)}>
        Back soon
      </Button>
      {open ? (
        <div className="absolute right-0 bottom-full z-[60] mb-1.5 flex gap-1 rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 p-1.5 shadow-[var(--shadow-2)]">
          {SPANS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => void set(m)}
              className="h-8 rounded-[9px] px-2.5 text-[12.5px] font-semibold text-dim outline-none transition-colors hover:bg-surface-2 hover:text-ink"
            >
              {m} min
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ a hand up */

/**
 * Asking for a gap in a busy call.
 *
 * Six people talking and one of them quiet is usually not somebody with
 * nothing to say - it is somebody who cannot find the gap. A hand is visible
 * to the whole room and drops by itself after ten minutes, so a forgotten one
 * does not sit up all evening.
 */
export function RaiseHand({ party }: { party: Party }) {
  const me = useSession((s) => s.me);
  const [busy, setBusy] = useState(false);
  const put = useParty(party.id);
  const toast = useToast();

  const mine = party.members.find((m) => m.userId === me?.id);
  const up = !!mine?.hand;
  const hands = party.members.filter((m) => m.hand).length;

  if (!party.isMember || !me) return null;

  async function toggle() {
    setBusy(true);
    try {
      const res = await api.post<{ party: Party }>('/api/parties/' + party.id + '/hand', { up: !up });
      put(res.party);
      if (!up) toast('Hand up - the room can see it', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      kind={up ? 'primary' : 'outline'}
      size="sm"
      disabled={busy}
      aria-pressed={up}
      icon={<Icon name="wave" size={15} />}
      onClick={() => void toggle()}
    >
      {up ? 'Hand up' : hands ? hands + ' hand' + (hands === 1 ? '' : 's') + ' up' : 'Raise a hand'}
    </Button>
  );
}

/* ------------------------------------------------------ joining as a pair */

/**
 * Bringing a friend in with you.
 *
 * Two people who want to play together should not have to race for the last
 * two seats one at a time and discover halfway that only one fitted. Both go
 * in or neither does.
 */
export function JoinWithFriend({ party, onClose }: { party: Party; onClose: () => void }) {
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const queryClient = useQueryClient();
  const toast = useToast();
  const [who, setWho] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  const free = party.size - party.memberCount;
  const term = q.trim().toLowerCase();
  const people = (friends.data?.friends ?? [])
    .map((f) => f.user)
    .filter((u) => !term || (u.username + ' ' + u.displayName).toLowerCase().includes(term));

  async function go2() {
    if (!who || busy) return;
    setBusy(true);
    try {
      const res = await api.post<{ party: Party }>('/api/parties/' + party.id + '/join-with', { username: who });
      queryClient.setQueryData(['party', party.id], (old: { party: Party } | undefined) => (old ? { ...old, party: res.party } : old));
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
      toast('You are both in', 'good');
      onClose();
      go('party/' + party.id);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Join with a friend"
      subtitle={free >= 2 ? 'Both of you go in together, or neither does.' : 'There is only one seat left - this needs two.'}
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={!who || busy || free < 2} onClick={() => void go2()}>
            {busy ? 'Joining…' : 'Join together'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Which friend?"
          className="h-10 w-full rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-3 text-[13.5px] shadow-[inset_0_0_0_1px_var(--line)] outline-none placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent)]"
        />
        <ul className="m-0 flex max-h-[300px] list-none flex-col gap-1 overflow-y-auto p-0">
          {people.map((u) => {
            const on = who === u.username;
            return (
              <li key={u.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => setWho(u.username)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] p-2 text-left outline-none transition-colors',
                    on ? 'bg-accent-soft' : 'hover:bg-surface-2'
                  )}
                >
                  <Avatar user={u} size={32} presence />
                  <UserName user={u} className="min-w-0 flex-1 truncate text-[13.5px] font-semibold" />
                  {on ? <Icon name="check" size={14} className="flex-none text-accent" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
